import BigNumber from 'bignumber.js'
import { BIG_ZERO } from '@donaswap/utils/bigNumber'
import { ChainId } from '@donaswap/sdk'
import { FLAME } from '@donaswap/tokens'
import { Address } from 'viem'

import { flameVaultV2ABI } from '../abis/IFlameVaultV2'
import { OnChainProvider } from '../types'
import { getFlameFlexibleSideVaultAddress, getFlameVaultAddress } from './getAddresses'

interface Params {
  flameVaultAddress?: Address
  chainId: ChainId
  provider: OnChainProvider
}

const balanceOfAbi = [
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

export const fetchPublicVaultData = async ({
  chainId,
  flameVaultAddress = getFlameVaultAddress(chainId),
  provider,
}: Params) => {
  try {
    const client = provider({ chainId })

    const [sharePrice, shares, totalLockedAmount, totalFlameInVault] = await client.multicall({
      contracts: [
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'getPricePerFullShare',
        },
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'totalShares',
        },
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'totalLockedAmount',
        },
        {
          abi: balanceOfAbi,
          address: FLAME[ChainId.BSC].address,
          functionName: 'balanceOf',
          args: [flameVaultAddress],
        },
      ],
      allowFailure: true,
    })

    const totalSharesAsBigNumber =
      shares.status === 'success' && shares.result ? new BigNumber(shares.result.toString()) : BIG_ZERO
    const totalLockedAmountAsBigNumber =
      totalLockedAmount.status === 'success' && totalLockedAmount.result
        ? new BigNumber(totalLockedAmount.result.toString())
        : BIG_ZERO
    const sharePriceAsBigNumber =
      sharePrice.status === 'success' && sharePrice.result ? new BigNumber(sharePrice.result.toString()) : BIG_ZERO

    return {
      totalShares: totalSharesAsBigNumber.toJSON(),
      totalLockedAmount: totalLockedAmountAsBigNumber.toJSON(),
      pricePerFullShare: sharePriceAsBigNumber.toJSON(),
      totalFlameInVault: totalFlameInVault.result ? new BigNumber(totalFlameInVault.result.toString()).toJSON() : '0',
    }
  } catch (error) {
    return {
      totalShares: null,
      totalLockedAmount: null,
      pricePerFullShare: null,
      totalFlameInVault: null,
    }
  }
}

export const fetchPublicFlexibleSideVaultData = async ({
  chainId,
  flameVaultAddress = getFlameFlexibleSideVaultAddress(chainId),
  provider,
}: Params) => {
  try {
    const client = provider({ chainId })

    const [sharePrice, shares, totalFlameInVault] = await client.multicall({
      contracts: [
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'getPricePerFullShare',
        },
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'totalShares',
        },
        {
          abi: balanceOfAbi,
          address: FLAME[ChainId.BSC].address,
          functionName: 'balanceOf',
          args: [flameVaultAddress],
        },
      ],
      allowFailure: true,
    })

    const totalSharesAsBigNumber = shares.status === 'success' ? new BigNumber(shares.result.toString()) : BIG_ZERO
    const sharePriceAsBigNumber =
      sharePrice.status === 'success' ? new BigNumber(sharePrice.result.toString()) : BIG_ZERO
    return {
      totalShares: totalSharesAsBigNumber.toJSON(),
      pricePerFullShare: sharePriceAsBigNumber.toJSON(),
      totalFlameInVault: new BigNumber((totalFlameInVault.result || '0').toString()).toJSON(),
    }
  } catch (error) {
    return {
      totalShares: null,
      pricePerFullShare: null,
      totalFlameInVault: null,
    }
  }
}

export const fetchVaultFees = async ({
  chainId,
  flameVaultAddress = getFlameVaultAddress(chainId),
  provider,
}: Params) => {
  try {
    const client = provider({ chainId })

    const [performanceFee, withdrawalFee, withdrawalFeePeriod] = await client.multicall({
      contracts: [
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'performanceFee',
        },
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'withdrawFee',
        },
        {
          abi: flameVaultV2ABI,
          address: flameVaultAddress,
          functionName: 'withdrawFeePeriod',
        },
      ],
      allowFailure: false,
    })

    return {
      performanceFee: Number(performanceFee),
      withdrawalFee: Number(withdrawalFee),
      withdrawalFeePeriod: Number(withdrawalFeePeriod),
    }
  } catch (error) {
    return {
      performanceFee: null,
      withdrawalFee: null,
      withdrawalFeePeriod: null,
    }
  }
}
